/**
 * Responsive Design Configuration
 * 
 * This file contains standardized configuration for responsive design
 * across the entire application. It provides consistent breakpoints,
 * spacing values, and utility functions for responsive behavior.
 */

import { Theme } from '@mui/material/styles';

// Standard breakpoints (in pixels)
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// Touch target sizes (in pixels) for different device types
export const touchTargets = {
  mobile: 48, // Minimum 48px for mobile touch targets (Apple & Google guidelines)
  tablet: 44, // Slightly smaller for tablets
  desktop: 32, // Standard size for desktop (mouse interaction)
};

// Standard spacing multipliers for responsive layouts
export const spacing = {
  xs: 1, // 8px (with default MUI theme spacing)
  sm: 2, // 16px
  md: 3, // 24px
  lg: 4, // 32px
  xl: 6, // 48px
};

// Container max-widths for different breakpoints
export const containerMaxWidths = {
  xs: '100%',
  sm: '600px',
  md: '960px',
  lg: '1280px',
  xl: '1920px',
};

// Font sizes for different breakpoints
export const fontSizes = {
  // Headers
  h1: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' }, 
  h2: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem' },
  h3: { xs: '1.25rem', sm: '1.5rem', md: '1.875rem' },
  h4: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
  h5: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
  h6: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
  
  // Body text
  body1: { xs: '0.875rem', sm: '1rem', md: '1rem' },
  body2: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' },
  
  // Other text elements
  button: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
  caption: { xs: '0.75rem', sm: '0.75rem', md: '0.75rem' },
};

// Common padding patterns for containers based on breakpoint
export const containerPadding = {
  xs: { px: spacing.xs, py: spacing.xs },
  sm: { px: spacing.sm, py: spacing.sm },
  md: { px: spacing.md, py: spacing.md },
  lg: { px: spacing.lg, py: spacing.md },
};

// Bottom navigation offset for mobile views
export const BOTTOM_NAV_HEIGHT = 64;

// Helper functions for responsive styling
export const getResponsivePadding = (theme: Theme) => ({
  xs: theme.spacing(spacing.xs),
  sm: theme.spacing(spacing.sm),
  md: theme.spacing(spacing.md),
  lg: theme.spacing(spacing.lg),
});

// Function to adjust for bottom navigation on mobile
export const withBottomNavOffset = (theme: Theme) => ({
  paddingBottom: `calc(${theme.spacing(spacing.lg)} + ${BOTTOM_NAV_HEIGHT}px)`,
});

// Define standard grid configurations for different layouts
export const gridLayouts = {
  // 1 column on mobile, 2 on tablet, 3 on desktop
  threeColumnResponsive: {
    xs: 12,
    sm: 6,
    md: 4,
  },
  
  // 1 column on mobile, 2 on tablet/desktop
  twoColumnResponsive: {
    xs: 12,
    sm: 6,
  },
  
  // 2 column on mobile, 3 on tablet, 4 on desktop
  denseGrid: {
    xs: 6,
    sm: 4,
    md: 3,
  },
  
  // Full width
  fullWidth: {
    xs: 12,
  },
  
  // Sidebar and main content (1:3 ratio)
  sidebarLayout: {
    sidebar: { xs: 12, sm: 4, md: 3 },
    content: { xs: 12, sm: 8, md: 9 },
  },
};

export default {
  breakpoints,
  touchTargets,
  spacing,
  containerMaxWidths,
  fontSizes,
  containerPadding,
  BOTTOM_NAV_HEIGHT,
  getResponsivePadding,
  withBottomNavOffset,
  gridLayouts,
};
